import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  AppTokenService,
  AuthAccessTokenDetails,
  OauthAEMConfig,
  UtilService
} from '@com-delta-idp/mnx-oauth';
import {
  ApiBuilder,
  AppConfigService,
  handleError,
  HttpHeadersPayload,
  HttpRequestMethods
} from '@com-delta-idp/mnx-utils';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import {
  BEARER,
  DEFAULT_TIMEOUT,
  HEADER,
  HTTP_HEADERS,
  MOST_UPCOMING_TRIP,
  MyTripEndpointKeys
} from '../../model/enums/most-upcoming-trips.enums';
import {
  MostUpcomingTrip,
  MostUpcomingTripParams
} from '../../model/enums/most-upcoming-trips.interface';

@Injectable({
  providedIn: 'root'
})
export class MostUpcomingTripsService {
  errorHandler = handleError.bind(this);

  constructor(
    private readonly http: HttpClient,
    private readonly appToken: AppTokenService,
    private readonly utils: UtilService,
    private readonly appConfig: AppConfigService
  ) {}

  handleError(httpErrorResponse: HttpErrorResponse): Observable<never> {
    return throwError(httpErrorResponse);
  }

  getHeaders = (tokenDetails: AuthAccessTokenDetails): HttpHeadersPayload => {
    return {
      Authorization: BEARER + ' ' + tokenDetails.access_token
    };
  };

  getToken(authConfig: OauthAEMConfig): Observable<AuthAccessTokenDetails> {
    const authCopy: OauthAEMConfig = {
      ...authConfig,
      SCOPE_VALUE_RM: `${authConfig.SCOPE_VALUE} ${authConfig.SAVED_SEARCH_GRAPHQL_SCOPE_VALUE}`,
      CLIENT_ID_VALUE: authConfig['HEADER_CLIENT_ID_VALUE'] ?? ''
    };
    return this.appToken.getAppToken(authCopy).pipe(
      map((token: AuthAccessTokenDetails) => {
        return token;
      })
    );
  }

  callMostUpcomingTrips = (
    tripDetails: MostUpcomingTripParams,
    authConfig: OauthAEMConfig
  ): Observable<MostUpcomingTrip> => {
    return this.getToken(authConfig).pipe(
      switchMap((tokenDetails: AuthAccessTokenDetails) => {
        if (tokenDetails.access_token) {
          return this.getMostUpcomingTripsDetails(tripDetails, tokenDetails);
        }
        return of({} as MostUpcomingTrip);
      })
    );
  };

  getMostUpcomingTripsDetails = (
    tripDetails: MostUpcomingTripParams,
    tokenDetails: AuthAccessTokenDetails
  ): Observable<MostUpcomingTrip> => {
    const transactionId = `${
      HTTP_HEADERS.MY_TRIP
    }_${this.utils.generateUniqueIdentifier()}`;
    return new ApiBuilder<unknown, MostUpcomingTrip>(this.http, this.appConfig)
      .overrideEndpoint([
        {
          src: MOST_UPCOMING_TRIP.SKY_MILE_NUMBER,
          target: tripDetails.skyMileNumber
        },
        {
          src: MOST_UPCOMING_TRIP.NO_OF_TRIPS,
          target: tripDetails.numberOfTrips
        },
        {
          src: MOST_UPCOMING_TRIP.TRANSACTION_ID,
          target: transactionId
        }
      ])
      .app(HEADER)
      .endpointKey(MyTripEndpointKeys.APP_TRIPS_UPCOMING_TRIP_URL)
      .method(HttpRequestMethods.POST)
      .httpHeaders(this.getHeaders(tokenDetails))
      .errorHandler(this.errorHandler)
      .timeOut(DEFAULT_TIMEOUT)
      .create()
      .pipe(
        map((response: MostUpcomingTrip) => response),
        catchError((err) => this.handleError(err))
      );
  };
}
